import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { AI_PRICES_STALE_DAYS, BIFI_MIN_GROUP, COUNTRY_CURRENCY, PLAN_LABELS, type AiModelPriceDTO, type PlatformBifiStatsDTO } from '@bifeai/shared';
import { BarChart, Button, Callout, Card, CardBody, CardHeader, EmptyState, Input, Kpi, PageHeader, Select, Skeleton, useToast } from '@bifeai/ui';
import { Clock, MessageCircle, MessagesSquare, Mic, Moon, Percent, ShoppingBasket, Store } from 'lucide-react';
import { useState } from 'react';
import { DateRangePicker, useDateRange } from '../components/DateRange';
import { api, errorMessage } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/format';
import { useCan } from '../lib/session';

const COUNTRY_NAMES: Record<string, string> = { AR: 'Argentina', UY: 'Uruguay' };
const num = (v: number) => v.toLocaleString('es-AR');
/** Números grandes abreviados para que entren en la tarjeta (1,2 M). */
const big = (v: number) => (v >= 1_000_000 ? `${(v / 1_000_000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} M` : num(v));
/** Monto en la moneda del país (pesos argentinos, uruguayos…). */
const money = (cents: number | null, country: string) =>
  cents === null
    ? 'Privado'
    : new Intl.NumberFormat('es-AR', { style: 'currency', currency: COUNTRY_CURRENCY[country] ?? 'ARS', maximumFractionDigits: 0 }).format(
        cents / 100,
      );

/** KPIs de bifi en todas las carnicerías, con cortes por país, provincia y localidad. Sin datos personales de clientes. */
export function BifiStatsPage() {
  const range = useDateRange('last30');
  const [country, setCountry] = useState('');
  const [province, setProvince] = useState('');
  const [locality, setLocality] = useState('');
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date());
  // "Todo": el último año (las estadísticas se piden de a un año como máximo).
  const from = range.range.from || new Date(new Date(`${today}T12:00:00Z`).getTime() - 365 * 86_400_000).toISOString().slice(0, 10);
  const to = range.range.to || today;
  const qs = new URLSearchParams({ from, to });
  if (country) qs.set('country', country);
  if (province) qs.set('province', province);
  if (locality) qs.set('locality', locality);
  const q = useQuery({
    queryKey: ['bifi-stats', qs.toString()],
    queryFn: () => api.get<PlatformBifiStatsDTO>(`/platform/bifi/stats?${qs.toString()}`),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const single = d && d.byCountry.length === 1 ? d.byCountry[0]! : null;

  return (
    <>
      <PageHeader
        title="Estadísticas de bifi"
        subtitle="Cuánto atiende y vende bifi en todas las carnicerías, por país, provincia y localidad. Sin datos personales de los clientes ni lo que factura cada carnicería."
      />
      <Card>
        <CardHeader title={`Período · ${range.label}`} actions={<DateRangePicker r={range} />} />
        <CardBody>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Select
              aria-label="País"
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                setProvince('');
                setLocality('');
              }}
              style={{ width: 'auto', minWidth: 180 }}
            >
              <option value="">Todos los países</option>
              {(d?.options.countries ?? []).map((c) => (
                <option key={c} value={c}>
                  {COUNTRY_NAMES[c] ?? c}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Provincia"
              value={province}
              onChange={(e) => {
                setProvince(e.target.value);
                setLocality('');
              }}
              style={{ width: 'auto', minWidth: 200 }}
            >
              <option value="">Todas las provincias</option>
              {(d?.options.provinces ?? []).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
            <Select aria-label="Localidad" value={locality} onChange={(e) => setLocality(e.target.value)} style={{ width: 'auto', minWidth: 200 }}>
              <option value="">Todas las localidades</option>
              {(d?.options.localities ?? []).map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
          </div>
        </CardBody>
      </Card>

      {!d ? (
        <Skeleton style={{ height: 320 }} />
      ) : (
        <>
          <div className="bf-kpi-grid">
            <Kpi
              label="Chats de clientes"
              tone="celeste"
              icon={<MessagesSquare />}
              value={big(d.chats)}
              delta={`${big(d.chatCustomers)} clientes distintos · ${big(d.chatsFirstTime)} por primera vez`}
              deltaMuted
            />
            <Kpi
              label="Pedidos que tomó"
              tone="green"
              icon={<ShoppingBasket />}
              value={big(d.orders)}
              delta={`${big(d.paidLink.count)} pagados con el link`}
              deltaMuted
            />
            <Kpi
              label="Conversión"
              tone="amber"
              icon={<Percent />}
              value={d.conversionPct === null ? '—' : `${d.conversionPct.toLocaleString('es-AR')} %`}
              delta="Chats que terminaron en pedido"
              deltaMuted
            />
            <Kpi
              label="Carnicerías con bifi"
              tone="red"
              icon={<Store />}
              value={num(d.tenantsWithBifi)}
              delta={`${num(d.activeTenants)} con mensajes en el período`}
              deltaMuted
            />
          </div>
          <div className="bf-kpi-grid">
            <Kpi
              label="Vendido por WhatsApp"
              tone="green"
              icon={<ShoppingBasket />}
              value={single ? money(single.amountCents, single.country) : d.byCountry.length ? 'Ver por país' : '—'}
              delta={
                single && single.amountCents === null
                  ? `Se muestra con ${BIFI_MIN_GROUP} carnicerías o más`
                  : single
                    ? `Venta cruzada ${money(single.crossSellCents, single.country)}`
                    : 'Cada país en su moneda, abajo'
              }
              deltaMuted
            />
            <Kpi
              label="Con el local cerrado"
              tone="celeste"
              icon={<Moon />}
              value={big(d.chatsOutOfHours)}
              delta={`Chats atendidos · ${big(d.ordersOutOfHours)} pedidos tomados`}
              deltaMuted
            />
            <Kpi
              label="Mensajes que contestó"
              tone="amber"
              icon={<MessageCircle />}
              value={big(d.messagesByBifi)}
              delta={`De ${big(d.messagesReceived)} recibidos · ${big(d.audios)} audios`}
              deltaMuted
            />
            <Kpi
              label="Costo estimado de la IA"
              tone="red"
              icon={<Clock />}
              value={`USD ${d.aiUsd.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              delta={`Venta cruzada: ${num(d.crossSell.accepted)} de ${num(d.crossSell.offered)} ofertas`}
              deltaMuted
            />
          </div>

          <Card>
            <CardHeader
              title="Por país"
              subtitle={`Lo vendido, en la moneda de cada país. Con menos de ${BIFI_MIN_GROUP} carnicerías la plata no se muestra: sería lo que factura una sola.`}
            />
            <CardBody>
              {d.byCountry.length === 0 ? (
                <EmptyState icon={<Mic />} title="Sin datos en este período" />
              ) : (
                <div className="bf-table-wrap">
                  <table className="bf-table">
                    <thead>
                      <tr>
                        <th>País</th>
                        <th style={{ textAlign: 'right' }}>Pedidos</th>
                        <th style={{ textAlign: 'right' }}>Vendido</th>
                        <th style={{ textAlign: 'right' }}>Venta cruzada</th>
                        <th style={{ textAlign: 'right' }}>Cobrado con link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.byCountry.map((c) => (
                        <tr key={c.country}>
                          <td>{COUNTRY_NAMES[c.country] ?? c.country}</td>
                          <td style={{ textAlign: 'right' }}>{num(c.orders)}</td>
                          <td style={{ textAlign: 'right' }}>{money(c.amountCents, c.country)}</td>
                          <td style={{ textAlign: 'right' }}>{money(c.crossSellCents, c.country)}</td>
                          <td style={{ textAlign: 'right' }}>{money(c.paidLinkCents, c.country)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Chats por día" subtitle={`${formatDate(d.period.from)} al ${formatDate(d.period.to)}`} />
            <CardBody>
              <DayBars rows={d.byDay} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Por provincia y localidad" subtitle="Sin montos: en una localidad con una sola carnicería se vería lo que factura." />
            <CardBody>
              <LocTable rows={d.byLocation} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Por carnicería" subtitle="Cuánto usan bifi. Lo que factura cada carnicería es privado y no se muestra." />
            <CardBody>
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Carnicería</th>
                      <th>Plan</th>
                      <th>Ubicación</th>
                      <th style={{ textAlign: 'right' }}>Chats</th>
                      <th style={{ textAlign: 'right' }}>Pedidos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.byTenant.map((t) => (
                      <tr key={t.tenantId}>
                        <td>{t.name}</td>
                        <td>{PLAN_LABELS[t.plan as keyof typeof PLAN_LABELS] ?? t.plan}</td>
                        <td className="bf-cell-sub">{[t.locality, t.province, COUNTRY_NAMES[t.country] ?? t.country].filter(Boolean).join(', ')}</td>
                        <td style={{ textAlign: 'right' }}>{num(t.chats)}</td>
                        <td style={{ textAlign: 'right' }}>{num(t.orders)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </>
      )}
      <AiPricesCard />
    </>
  );
}

function LocTable({ rows }: { rows: PlatformBifiStatsDTO['byLocation'] }) {
  if (rows.length === 0) return <EmptyState icon={<Store />} title="Sin carnicerías con bifi en este filtro" />;
  return (
    <div className="bf-table-wrap">
      <table className="bf-table">
        <thead>
          <tr>
            <th>País</th>
            <th>Provincia</th>
            <th>Localidad</th>
            <th style={{ textAlign: 'right' }}>Carnicerías</th>
            <th style={{ textAlign: 'right' }}>Chats</th>
            <th style={{ textAlign: 'right' }}>Pedidos</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.country}|${r.province}|${r.locality}`}>
              <td>{COUNTRY_NAMES[r.country] ?? r.country}</td>
              <td>{r.province ?? '—'}</td>
              <td>{r.locality ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>{num(r.tenants)}</td>
              <td style={{ textAlign: 'right' }}>{num(r.chats)}</td>
              <td style={{ textAlign: 'right' }}>{num(r.orders)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DayBars({ rows }: { rows: PlatformBifiStatsDTO['byDay'] }) {
  return (
    <BarChart
      ariaLabel="Chats de clientes por día"
      data={rows.map((r) => ({ key: r.date, label: formatDate(r.date).slice(0, 5), value: r.chats }))}
      tip={(i) => {
        const r = rows[i]!;
        return { title: formatDate(r.date), lines: [`${num(r.chats)} chats`, `${num(r.orders)} pedidos`] };
      }}
    />
  );
}

/** Precios de la IA (USD por millón de tokens). Anthropic no los publica por API: se actualizan acá. */
function AiPricesCard() {
  const can = useCan();
  const prices = useQuery({ queryKey: ['bifi', 'ai-prices'], queryFn: () => api.get<AiModelPriceDTO[]>('/platform/bifi/ai-prices') });
  const list = prices.data ?? [];
  const oldest = list.length ? Math.min(...list.map((p) => new Date(p.updatedAt).getTime())) : Date.now();
  const stale = Date.now() - oldest > AI_PRICES_STALE_DAYS * 86_400_000;
  return (
    <Card>
      <CardHeader
        title="Precios de la IA"
        subtitle="Con esto se estima el costo de bifi en las estadísticas (USD por millón de tokens). Si Anthropic cambia el precio, actualizalo acá y todo se recalcula."
      />
      <CardBody>
        {stale && (
          <Callout tone="warning">
            Hace más de {AI_PRICES_STALE_DAYS} días que no se revisan. Chequealos en la página de precios de Anthropic (anthropic.com/pricing).
          </Callout>
        )}
        <div style={{ display: 'grid', gap: 14 }}>
          {list.map((p) => (
            <PriceRow key={p.model} price={p} editable={can('prices.manage') && !p.retiredAt} />
          ))}
        </div>
        {can('prices.manage') && <NewModel />}
      </CardBody>
    </Card>
  );
}

function PriceRow({ price, editable }: { price: AiModelPriceDTO; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState({ inputUsd: String(price.inputUsd), outputUsd: String(price.outputUsd), cacheReadUsd: String(price.cacheReadUsd) });
  const [busy, setBusy] = useState(false);
  const dirty = v.inputUsd !== String(price.inputUsd) || v.outputUsd !== String(price.outputUsd) || v.cacheReadUsd !== String(price.cacheReadUsd);
  async function save() {
    setBusy(true);
    try {
      const toNum = (s: string) => Number(s.replace(',', '.'));
      await api.put(`/platform/bifi/ai-prices/${price.model}`, {
        inputUsd: toNum(v.inputUsd),
        outputUsd: toNum(v.outputUsd),
        cacheReadUsd: toNum(v.cacheReadUsd),
      });
      await qc.invalidateQueries({ queryKey: ['bifi'] });
      await qc.invalidateQueries({ queryKey: ['bifi-stats'] });
      toast.success(`${price.label}: precio actualizado`, 'Las estadísticas ya usan el precio nuevo.');
    } catch (err) {
      toast.error('No se pudo guardar', errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  const field = (key: keyof typeof v, label: string) => (
    <label className="bf-cell-sub" style={{ display: 'grid', gap: 4 }}>
      {label}
      <Input inputMode="decimal" value={v[key]} disabled={!editable} onChange={(e) => setV({ ...v, [key]: e.target.value })} style={{ width: 120 }} />
    </label>
  );
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 260 }}>
        <strong>{price.label}</strong>
        <div className="bf-cell-sub">
          {price.model} ·{' '}
          {price.retiredAt ? `Retirado por Anthropic el ${formatDate(price.retiredAt)}` : `Actualizado ${formatDateTime(price.updatedAt)}`}
        </div>
      </div>
      {field('inputUsd', 'Entrada')}
      {field('outputUsd', 'Salida')}
      {field('cacheReadUsd', 'Lectura de caché')}
      {editable && (
        <Button variant="primary" loading={busy} disabled={!dirty} onClick={() => void save()}>
          Guardar
        </Button>
      )}
    </div>
  );
}

/** Modelo nuevo de Anthropic: con su precio cargado ya se puede elegir en Agentes IA (probarlo en el simulador y publicarlo). */
function NewModel() {
  const qc = useQueryClient();
  const toast = useToast();
  const empty = { model: '', label: '', inputUsd: '', outputUsd: '', cacheReadUsd: '' };
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(empty);
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <div style={{ marginTop: 16 }}>
        <Button onClick={() => setOpen(true)}>Agregar modelo</Button>
      </div>
    );
  }
  async function save() {
    setBusy(true);
    try {
      const toNum = (s: string) => Number(s.replace(',', '.'));
      await api.post('/platform/bifi/ai-prices', {
        model: v.model.trim(),
        label: v.label.trim(),
        inputUsd: toNum(v.inputUsd),
        outputUsd: toNum(v.outputUsd),
        cacheReadUsd: toNum(v.cacheReadUsd),
      });
      await qc.invalidateQueries({ queryKey: ['bifi'] });
      toast.success(`${v.label} cargado`, 'Ya se puede elegir en Agentes IA: probalo en el simulador antes de publicarlo.');
      setV(empty);
      setOpen(false);
    } catch (err) {
      toast.error('No se pudo cargar', errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  const field = (key: keyof typeof v, label: string, width: number, placeholder = '') => (
    <label className="bf-cell-sub" style={{ display: 'grid', gap: 4 }}>
      {label}
      <Input value={v[key]} placeholder={placeholder} onChange={(e) => setV({ ...v, [key]: e.target.value })} style={{ width }} />
    </label>
  );
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        marginTop: 18,
        paddingTop: 18,
        borderTop: '1px solid var(--bf-line)',
      }}
    >
      {field('model', 'Nombre exacto en Anthropic', 220, 'claude-…')}
      {field('label', 'Cómo se muestra', 180, 'Claude …')}
      {field('inputUsd', 'Entrada', 100)}
      {field('outputUsd', 'Salida', 100)}
      {field('cacheReadUsd', 'Lectura de caché', 120)}
      <Button
        variant="primary"
        loading={busy}
        disabled={!v.model || !v.label || !v.inputUsd || !v.outputUsd || !v.cacheReadUsd}
        onClick={() => void save()}
      >
        Cargar
      </Button>
      <Button variant="ghost" onClick={() => setOpen(false)}>
        Cancelar
      </Button>
    </div>
  );
}
